import { IconUser } from '@tabler/icons-react';
import { hasCustomClassCategories, isDefined } from '@zorro/common-utils';
import { Menu } from '@zorro/design-system/v2/Menu';
import {
  getClassDefinitionYear,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';

import { useAnalytics } from '../../Analytics';
import {
  actionsMenuTooltipLabels,
  getOnboardingPeriodsStatus,
} from '../ActionsMenuUtils';
import { ActionsMenuPrerequisitesTooltip } from '../ActionsRequirementTooltip';
import { ActionsSubMenu } from '../ActionsSubMenu';
import { ActionsMenuSection } from '../EmployeeActionsUtils';
import { EmployeeActionsOverlays } from '../employeeActions.consts';

type EmployeeAdministrationSectionProps = {
  setOverlayId: ActionsMenuSection['setOverlayId'];
  recordsForActions: ActionsMenuSection['recordsForActions'];
  employerId?: string;
  isInEnrollmentsDatatable: boolean;
};

const EmployeeAdministrationSection = ({
  setOverlayId,
  recordsForActions,
  employerId,
  isInEnrollmentsDatatable,
}: EmployeeAdministrationSectionProps) => {
  const { isZorroOperations } = useRoles();
  const { sendEvent } = useAnalytics();

  const { data: openEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: employerId && [employerId],
  });

  const classDefinitionYear = getClassDefinitionYear(
    openEnrollmentPeriods,
    employerId
  );

  const { data: customAttributes } = useMonolithQuery({
    method: 'allowanceModelControllerGetCustomAttributesForEmployerByYear',
    params: employerId && [employerId, classDefinitionYear],
  });

  const hasCustomClassCategory = hasCustomClassCategories(
    customAttributes ?? []
  );

  // Custom class category is a period-scoped write, so every selected record
  // must have an onboarding period for the action to be available.
  const { areAllOnboardingPeriodsExist } =
    getOnboardingPeriodsStatus(recordsForActions);

  return (
    <ActionsSubMenu title="Employee administration" icon={IconUser}>
      {!isInEnrollmentsDatatable && (
        <>
          <Menu.Item
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'employee_administration',
                action_name: 'TERMINATE_EMPLOYEE',
              });
              setOverlayId(EmployeeActionsOverlays.TERMINATE_EMPLOYEE);
            }}
          >
            {EmployeeActionsOverlays.TERMINATE_EMPLOYEE}
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'employee_administration',
                action_name: 'SINGLE_SET_LEAVE_OF_ABSENCE',
              });
              setOverlayId(EmployeeActionsOverlays.SINGLE_SET_LEAVE_OF_ABSENCE);
            }}
          >
            {EmployeeActionsOverlays.SINGLE_SET_LEAVE_OF_ABSENCE}
          </Menu.Item>
        </>
      )}

      {isZorroOperations && !isInEnrollmentsDatatable && (
        <Menu.Item
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'employee_administration',
              action_name: 'UPDATE_ELIGIBILITY',
            });
            setOverlayId(EmployeeActionsOverlays.UPDATE_ELIGIBILITY);
          }}
        >
          {EmployeeActionsOverlays.UPDATE_ELIGIBILITY}
        </Menu.Item>
      )}

      {hasCustomClassCategory && (
        <ActionsMenuPrerequisitesTooltip
          title="Change custom class"
          isAllEqual={isDefined(employerId) && areAllOnboardingPeriodsExist}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          prerequisites={[
            {
              isValid: isDefined(employerId),
              label: actionsMenuTooltipLabels.isSingleEmployer,
            },
            {
              isValid: areAllOnboardingPeriodsExist,
              label: actionsMenuTooltipLabels.areAllOnboardingPeriodsExist,
            },
          ]}
        >
          <Menu.Item
            disabled={!employerId || !areAllOnboardingPeriodsExist}
            onClick={() => {
              void sendEvent('click_action', {
                action_category: 'employee_administration',
                action_name: 'CHANGE_EMPLOYEE_CUSTOM_CLASS',
              });
              setOverlayId(
                EmployeeActionsOverlays.CHANGE_EMPLOYEE_CUSTOM_CLASS
              );
            }}
          >
            {EmployeeActionsOverlays.CHANGE_EMPLOYEE_CUSTOM_CLASS}
          </Menu.Item>
        </ActionsMenuPrerequisitesTooltip>
      )}

      <ActionsMenuPrerequisitesTooltip
        title="Change sub entity"
        isAllEqual={isDefined(employerId)}
        isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        prerequisites={[
          {
            isValid: isDefined(employerId),
            label: actionsMenuTooltipLabels.isSingleEmployer,
          },
        ]}
      >
        <Menu.Item
          disabled={!employerId}
          onClick={() => {
            void sendEvent('click_action', {
              action_category: 'employee_administration',
              action_name: 'CHANGE_EMPLOYEE_SUB_ENTITY',
            });
            setOverlayId(EmployeeActionsOverlays.CHANGE_EMPLOYEE_SUB_ENTITY);
          }}
        >
          {EmployeeActionsOverlays.CHANGE_EMPLOYEE_SUB_ENTITY}
        </Menu.Item>
      </ActionsMenuPrerequisitesTooltip>
    </ActionsSubMenu>
  );
};

export { EmployeeAdministrationSection };
